import React, { useState } from 'react';
import { api, useLanguage } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { 
  Share2, Facebook, MessageCircle, Mail, Copy, Check,
  Twitter, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

// Platform icons
// LinkedIn Icon
const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

/**
 * Generate platform-specific share content
 * Each platform has optimized text for better engagement
 */
const generatePlatformShareContent = ({ entityType, title, description, location, companyName, category, shareUrl }) => {
  const shortDesc = description ? description.substring(0, 100) + (description.length > 100 ? '...' : '') : '';
  const locationText = location ? ` in ${location}` : '';
  const companyText = companyName ? ` at ${companyName}` : '';
  
  // Entity type labels
  const entityLabels = {
    job: 'job opportunity',
    service: 'service',
    rental: 'equipment rental',
    auto_service: 'auto service',
    subcontract: 'project',
    company: 'company'
  };
  const entityLabel = entityLabels[entityType] || 'item';

  return {
    // Facebook: Conversational + local service tone
    facebook: {
      text: `Hey! Check out this ${entityLabel}${locationText} 👀\n\n"${title}"${companyText}\n\nKnow someone who might be interested? Share it! 🙌`,
      hashtags: '#jobs #hiring #opportunity #work'
    },
    
    // LinkedIn: Professional B2B tone
    linkedin: {
      text: `🔔 ${entityType === 'job' ? 'New Opportunity' : 'Professional Service Available'}\n\n${title}${companyText}${locationText}\n\n${shortDesc}\n\nLooking for qualified professionals or know someone who fits? Let's connect.\n\n#${category} #opportunity #business`,
    },
    
    // Twitter/X: Concise with hashtags
    twitter: {
      text: `${entityType === 'job' ? '🚀 Hiring:' : '✨'} ${title}${locationText}\n\n${shortDesc ? shortDesc.substring(0, 80) + '...' : ''}\n\n#${entityType} #${category}`,
    },
    
    // WhatsApp: Casual, direct, mobile-friendly
    whatsapp: {
      text: `Hi! I found this ${entityLabel} that might interest you:\n\n*${title}*${companyText}${locationText}\n\n${shortDesc}`,
    },
    
    // Messenger: Casual, direct
    messenger: {
      text: `Check this out! ${title}${locationText}`,
    },
    
    // TikTok: Short hook + CTA
    tiktok: {
      text: `${entityType === 'job' ? '💼 JOB ALERT' : '🔥 CHECK THIS OUT'}\n\n${title}${locationText}\n\nLink in bio or copy below! 👇`,
      caption: `${title} ${locationText} #keikka #${entityType} #${category} #fyp #viral`,
      hashtags: `#keikka #${entityType} #${category} #fyp #viral #work`
    },
    
    // Email: Clean subject + professional body
    email: {
      subject: `${entityType === 'job' ? 'Job Opportunity' : 'Interesting Find'}: ${title}${locationText}`,
      body: `Hi,\n\nI came across this ${entityLabel} and thought you might be interested:\n\n${title}${companyText}${locationText}\n\n${shortDesc}\n\nView details: ${shareUrl}\n\nBest regards`,
    },
    
    // Copy Link: SEO-friendly (used for clipboard)
    copy: {
      text: `${title} | ${entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1)}${locationText} | KEIKKA`,
    },
    
    // Default fallback
    default: {
      text: `${title}${locationText} - KEIKKA`,
    }
  };
};

/**
 * ShareButton Component
 * Renders a share button with popover for multiple platforms
 * Includes platform-specific optimized share text
 */
export const ShareButton = ({ 
  entityType, // 'job' | 'service' | 'rental' | 'auto_service' | 'subcontract'
  entityId,
  title,
  description = '',
  location = '',
  companyName = '',
  category = '',
  className = "",
  iconOnly = false
}) => {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showTikTokModal, setShowTikTokModal] = useState(false);

  // Build share URL with referral tracking
  const urlPrefix = {
    job: 'jobs',
    service: 'services',
    rental: 'rentals',
    auto_service: 'auto',
    subcontract: 'subcontracting',
    company: 'company'
  }[entityType];

  const baseUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;
  
  // For entities that support server-rendered Open Graph previews we fetch
  // the canonical (slug) URL from the backend. This ensures Facebook,
  // LinkedIn, WhatsApp etc scrape the *actual* job title/description
  // instead of the generic homepage OG. Currently wired up for `job`.
  const [canonical, setCanonical] = useState({
    canonicalUrl: `${baseUrl}/${urlPrefix}/${entityId}`,
    // Backend share URL: the crawler-facing endpoint. Same-URL when we
    // don't have a dedicated OG route for this entity type yet.
    shareUrl: `${baseUrl}/${urlPrefix}/${entityId}`,
  });

  React.useEffect(() => {
    if (entityType !== 'job' || !entityId) return;
    let cancelled = false;
    api.get(`/og/jobs/${entityId}/canonical`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        if (data.canonical_url && data.share_url) {
          setCanonical({
            canonicalUrl: data.canonical_url,
            shareUrl: data.share_url,
          });
        }
      })
      .catch(() => { /* fallback to UUID URLs already set above */ });
    return () => { cancelled = true; };
  }, [entityType, entityId]);

  // Generate referral URL with UTM parameters — appended to whichever base
  // (canonical pretty URL for users, share URL for OG-scraped platforms).
  const withUtm = (rawUrl, platform) => {
    try {
      const url = new URL(rawUrl);
      url.searchParams.set('utm_source', platform);
      url.searchParams.set('utm_medium', 'social');
      url.searchParams.set('utm_campaign', 'share');
      url.searchParams.set('ref', entityType);
      return url.toString();
    } catch (_) {
      return rawUrl;
    }
  };

  // Facebook / LinkedIn / Twitter / WhatsApp: their crawlers must hit the
  // backend OG page so they see the *job-specific* title & image, not the
  // generic homepage. Other platforms (email, copy, native, tiktok) use
  // the pretty canonical URL that a human would want to see.
  const OG_PLATFORMS = new Set(['facebook', 'linkedin', 'twitter', 'whatsapp', 'messenger']);
  const generateShareUrl = (platform) => {
    const base = OG_PLATFORMS.has(platform) ? canonical.shareUrl : canonical.canonicalUrl;
    return withUtm(base, platform);
  };

  const shareUrl = canonical.canonicalUrl; // Base URL for display
  
  // Generate platform-specific share text
  const shareContent = generatePlatformShareContent({
    entityType,
    title,
    description,
    location,
    companyName,
    category,
    shareUrl
  });

  const handleShare = async (platform) => {
    setSharing(true);
    
    try {
      // Track share event
      await api.post(`/public/share/${entityType}/${entityId}?platform=${platform}`);
    } catch (error) {
      console.error('Failed to track share:', error);
    }
    
    const content = shareContent[platform] || shareContent.default;
    const trackingUrl = generateShareUrl(platform); // URL with referral tracking
    const encodedUrl = encodeURIComponent(trackingUrl);
    const encodedText = encodeURIComponent(content.text);
    
    // Platform-specific share URLs with optimized text and referral tracking
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
      whatsapp: `https://wa.me/?text=${encodedText}%0A%0A${encodedUrl}`,
      messenger: `https://www.facebook.com/dialog/send?link=${encodedUrl}&app_id=966242223397117&redirect_uri=${encodedUrl}`,
      email: `mailto:?subject=${encodeURIComponent(content.subject || content.text)}&body=${encodeURIComponent(content.body || content.text + '\n\n' + trackingUrl)}`,
    };

    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(trackingUrl); // Copy URL with tracking
        setCopied(true);
        toast.success(t('share.linkCopied'));
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        toast.error('Failed to copy link');
      }
      setSharing(false);
      setIsOpen(false);
    } else if (platform === 'tiktok') {
      // Open TikTok modal instead of direct share
      setShowTikTokModal(true);
      setIsOpen(false);
      setSharing(false);
    } else {
      // Open share URL in new window; if popup is blocked, fall back to
      // copying the canonical link so the user can paste it manually.
      const url = shareUrls[platform];
      if (url) {
        const popup = window.open(url, '_blank', 'width=600,height=500,noopener');
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          try {
            await navigator.clipboard.writeText(trackingUrl);
            toast.info(t('share.popupBlockedCopied') || 'Popup blocked — link copied to clipboard');
          } catch (_) {
            toast.error(t('share.popupBlocked') || 'Popup blocked. Please allow popups to share.');
          }
        }
      }
      setSharing(false);
      setIsOpen(false);
    }
  };

  const handleCopyForTikTok = async () => {
    try {
      await navigator.clipboard.writeText(generateShareUrl('tiktok'));
      setCopied(true);
      toast.success(t('share.linkCopied'));
      setTimeout(() => setCopied(false), 2000);
      // Track the share event
      await api.post(`/public/share/${entityType}/${entityId}?platform=tiktok`);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const platforms = [
    { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'hover:bg-blue-500/10 hover:text-blue-600' },
    { id: 'linkedin', name: 'LinkedIn', icon: LinkedInIcon, color: 'hover:bg-blue-700/10 hover:text-blue-700' },
    { id: 'whatsapp', name: 'WhatsApp', icon: WhatsAppIcon, color: 'hover:bg-green-500/10 hover:text-green-600' },
    { id: 'messenger', name: 'Messenger', icon: MessageCircle, color: 'hover:bg-blue-400/10 hover:text-blue-500' },
    { id: 'twitter', name: 'Twitter/X', icon: Twitter, color: 'hover:bg-gray-500/10 hover:text-gray-700' },
    { id: 'tiktok', name: 'TikTok', icon: TikTokIcon, color: 'hover:bg-pink-500/10 hover:text-pink-600' },
    { id: 'email', name: 'Email', icon: Mail, color: 'hover:bg-orange-500/10 hover:text-orange-600' },
    { id: 'copy', name: copied ? 'Copied!' : 'Copy Link', icon: copied ? Check : Copy, color: 'hover:bg-gray-500/10' }
  ];

  // Check if native share is supported (mobile)
  const supportsNativeShare = typeof navigator !== 'undefined' && navigator.share;

  // Handle native share for mobile
  const handleNativeShare = async () => {
    if (!supportsNativeShare) return false;
    
    try {
      await navigator.share({
        title: title,
        text: shareContent.default.text,
        url: generateShareUrl('native') // Use tracking URL
      });
      
      // Track as native share
      await api.post(`/public/share/${entityType}/${entityId}?platform=native`);
      return true;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Native share failed:', error);
      }
      return false;
    }
  };

  // Handle share button click - mobile native or desktop popover
  const handleShareClick = async () => {
    // On mobile, try native share first
    if (supportsNativeShare && window.innerWidth < 768) {
      const success = await handleNativeShare();
      if (success) return;
    }
    // Fall back to popover
    setIsOpen(true);
  };

  return (
    <>
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {iconOnly ? (
          <Button 
            variant="ghost" 
            size="icon" 
            className={className}
            data-testid={`share-button-${entityType}-${entityId}`}
            onClick={supportsNativeShare && window.innerWidth < 768 ? handleShareClick : undefined}
          >
            <Share2 className="h-4 w-4" />
          </Button>
        ) : (
          <Button 
            variant="outline" 
            size="sm" 
            className={className}
            data-testid={`share-button-${entityType}-${entityId}`}
            onClick={supportsNativeShare && window.innerWidth < 768 ? handleShareClick : undefined}
          >
            <Share2 className="h-4 w-4 mr-2" />
            {t('share.share')}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="end">
        <div className="space-y-1">
          <p className="text-sm font-medium px-2 py-1 text-muted-foreground">
            {t('share.shareOn')}
          </p>
          {platforms.map((platform) => {
            const Icon = platform.icon;
            return (
              <button
                key={platform.id}
                onClick={() => handleShare(platform.id)}
                disabled={sharing}
                className={`w-full flex items-center gap-3 px-2 py-2 rounded-md text-sm transition-colors ${platform.color}`}
                data-testid={`share-${platform.id}`}
              >
                <Icon className="h-5 w-5" />
                <span>{platform.name}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>

      {/* TikTok Share Modal */}
      <Dialog open={showTikTokModal} onOpenChange={setShowTikTokModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TikTokIcon />
              {t('share.shareOnTikTok')}
            </DialogTitle>
            <DialogDescription>
              {t('share.tiktokInstructions')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Shareable Link with tracking */}
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">
                Link to share
              </label>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={generateShareUrl('tiktok')}
                  className="flex-1 px-3 py-2 text-sm border rounded-md bg-muted"
                />
                <Button
                  size="sm"
                  variant={copied ? "default" : "outline"}
                  onClick={handleCopyForTikTok}
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Suggested Caption - Platform Optimized */}
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">
                {t('share.suggestedCaption')}
              </label>
              <div className="p-3 bg-muted rounded-md text-sm whitespace-pre-line">
                {shareContent.tiktok.text}
              </div>
            </div>
            
            {/* Hashtags */}
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">
                Hashtags
              </label>
              <div className="p-2 bg-brand/5 rounded-md text-sm text-brand">
                {shareContent.tiktok.hashtags}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowTikTokModal(false)}>
              {t('common.close')}
            </Button>
            <Button
              onClick={() => window.open('https://www.tiktok.com', '_blank')}
              className="bg-black hover:bg-gray-800"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {t('share.openTikTok')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Inline Share Buttons (horizontal layout)
 */
export const ShareButtonsInline = ({ 
  entityType, 
  entityId, 
  title,
  description = '',
  location = '',
  size = 'sm'
}) => {
  const [canonical, setCanonical] = React.useState(null);

  React.useEffect(() => {
    if (entityType !== 'job' || !entityId) return;
    let cancelled = false;
    api.get(`/og/jobs/${entityId}/canonical`)
      .then((res) => { if (!cancelled) setCanonical(res.data || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [entityType, entityId]);

  const urlPrefix = {
    job: 'jobs',
    service: 'services',
    rental: 'rentals',
    auto_service: 'auto',
    subcontract: 'subcontracting'
  }[entityType];
  const baseUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;
  const canonicalUrl = canonical?.canonical_url || `${baseUrl}/${urlPrefix}/${entityId}`;
  const shareTargetUrl = canonical?.share_url || canonicalUrl;

  const handleShare = async (platform) => {
    // Generate platform-specific content
    const content = generatePlatformShareContent({
      entityType,
      title,
      description,
      location,
      shareUrl: canonicalUrl
    });

    const platformContent = content[platform] || content.default;
    // Facebook/Twitter/WhatsApp scrape the URL for OG — hand them the
    // backend share URL so they display the actual job preview.
    const OG_PLATFORMS = new Set(['facebook', 'twitter', 'whatsapp']);
    const rawTarget = OG_PLATFORMS.has(platform) ? shareTargetUrl : canonicalUrl;
    const encodedUrl = encodeURIComponent(rawTarget);
    const encodedText = encodeURIComponent(platformContent.text);

    try {
      await api.post(`/public/share/${entityType}/${entityId}?platform=${platform}`);
    } catch (error) {
      console.error('Failed to track share:', error);
    }

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
      whatsapp: `https://wa.me/?text=${encodedText}%0A%0A${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`
    };

    if (shareUrls[platform]) {
      const popup = window.open(shareUrls[platform], '_blank', 'width=600,height=500,noopener');
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        try {
          await navigator.clipboard.writeText(canonicalUrl);
          toast.info('Popup blocked — link copied');
        } catch (_) {
          toast.error('Popup blocked. Please allow popups to share.');
        }
      }
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(canonicalUrl);
      await api.post(`/public/share/${entityType}/${entityId}?platform=copy`);
      toast.success('Link copied!');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const iconSize = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';

  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleShare('facebook')}
        className="hover:bg-blue-500/10 hover:text-blue-600"
        data-testid="share-inline-facebook"
      >
        <Facebook className={iconSize} />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleShare('whatsapp')}
        className="hover:bg-green-500/10 hover:text-green-600"
        data-testid="share-inline-whatsapp"
      >
        <WhatsAppIcon />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleShare('twitter')}
        className="hover:bg-gray-500/10"
        data-testid="share-inline-twitter"
      >
        <Twitter className={iconSize} />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={copyLink}
        className="hover:bg-gray-500/10"
        data-testid="share-inline-copy"
      >
        <Copy className={iconSize} />
      </Button>
    </div>
  );
};

export default ShareButton;
